import * as React from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Typography,
} from "@material-ui/core";

import { User } from "../../userTypes";
import PosConsoleApi from "app/utils/PosConsoleApi";

type Props = {
  user: User;
  onClose: () => void;
};

type State = {
  documentType: "id" | "passport";
  frontFile: File | null;
  backFile: File | null;
  uploading: boolean;
};

export default class ManualVerificationUploadDocumentModal extends React.Component<
  Props,
  State
> {
  state: State = {
    documentType: "id",
    frontFile: null,
    backFile: null,
    uploading: false,
  };

  selectedFile = (files: FileList | null): File | null => {
    return files && files.length > 0 ? files[0] : null;
  };

  canSubmit = (): boolean => {
    return (
      !!this.state.frontFile &&
      !!this.props.user.external_reference &&
      !this.state.uploading
    );
  };

  uploadDocument = async () => {
    if (!this.canSubmit()) {
      return;
    }

    const data: {
      external_reference: string;
      document_type: string;
      id_photo_front: File;
      id_photo_back?: File;
    } = {
      external_reference: this.props.user.external_reference as string,
      document_type: this.state.documentType,
      id_photo_front: this.state.frontFile as File,
    };
    if (this.state.backFile) {
      data.id_photo_back = this.state.backFile;
    }

    this.setState({ uploading: true });

    try {
      const response = await PosConsoleApi.postMultipart(
        "user_verification/document",
        data
      );
      const body = await response.json().catch(() => null);

      if (!response.ok) {
        const message =
          body && body.message
            ? body.message
            : "Failed to upload the document. Please try again.";
        throw new Error(message);
      }

      window.location.reload();
    } catch (error) {
      this.setState({ uploading: false });
      alert(error instanceof Error ? error.message : "Failed to upload the document. Please try again.");
    }
  };

  render() {
    return (
      <Dialog open={true} onClose={() => this.props.onClose()}>
        <DialogTitle>Upload ID/passport</DialogTitle>
        <DialogContent>
          <Typography style={{ marginBottom: "16px" }}>
            Attach an ID or passport image for this user so it can be used
            during manual verification. Original verification images are kept.
          </Typography>
          <FormControl style={{ marginBottom: "16px", display: "block" }}>
            <FormLabel>Document type</FormLabel>
            <RadioGroup
              value={this.state.documentType}
              onChange={(_event, value) =>
                this.setState({
                  documentType: value as "id" | "passport",
                })
              }
              style={{ flexDirection: "row" }}
            >
              <FormControlLabel value="id" control={<Radio />} label="ID" />
              <FormControlLabel
                value="passport"
                control={<Radio />}
                label="Passport"
              />
            </RadioGroup>
          </FormControl>
          <div style={{ marginBottom: "16px" }}>
            <Typography>Front image (required)</Typography>
            <input
              accept="image/jpeg,image/png,.jpg,.jpeg,.png"
              type="file"
              onChange={(event) =>
                this.setState({
                  frontFile: this.selectedFile(event.target.files),
                })
              }
            />
          </div>
          <div>
            <Typography>Back image (optional)</Typography>
            <input
              accept="image/jpeg,image/png,.jpg,.jpeg,.png"
              type="file"
              onChange={(event) =>
                this.setState({
                  backFile: this.selectedFile(event.target.files),
                })
              }
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button color="secondary" onClick={this.props.onClose}>
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={this.uploadDocument}
            disabled={!this.canSubmit()}
          >
            {this.state.uploading ? "Uploading..." : "Upload"}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
}
